import React, { useState } from "react";
import {
  CheckCircle,
  Phone,
  Place,
  Star,
  Bolt,
  Plumbing,
  Whatshot,
  WaterDrop,
  Sensors,
  Build,
  Verified,
  AttachMoney,
  Schedule,
  ThumbUp
} from "@mui/icons-material";

import {
  Page,
  Hero,
  HeroInner,
  HeroCopy,
  Eyebrow,
  HeroTitle,
  HeroSubtitle,
  HeroBullets,
  HeroCtas,
  PrimaryCall,
  GhostCta,
  HeroRating,
  FormCard,
  FormHeading,
  OfferTag,
  Field,
  Row,
  SubmitButton,
  PrivacyNote,
  Honeypot,
  ErrorMessage,
  TrustStrip,
  TrustInner,
  Section,
  SectionInner,
  SectionHead,
  ServiceGrid,
  ServiceCard,
  AreaGrid,
  AreaCard,
  WhyGrid,
  WhyItem,
  ReviewGrid,
  ReviewCard,
  CtaBand,
  Footer,
  MobileCallBar,
  SuccessCard
} from "./home.styles";

const PHONE = "951-438-7540";
const PHONE_HREF = "tel:9514387540";

const SERVICES = [
  {
    icon: <Bolt />,
    title: "24/7 Emergency Plumbing",
    desc:
      "Burst pipes, overflowing toilets, no hot water — a live plumber answers and dispatches fast, day or night."
  },
  {
    icon: <Plumbing />,
    title: "Drain Cleaning & Rooter",
    desc:
      "Hydro-jetting and rooter service to clear stubborn clogs in sinks, showers, and main lines for good."
  },
  {
    icon: <Whatshot />,
    title: "Water Heater Repair & Install",
    desc:
      "Tank and tankless repair, replacement, and same-day installs from licensed technicians."
  },
  {
    icon: <WaterDrop />,
    title: "Leak Detection & Repair",
    desc:
      "Pinpoint slab, wall, and underground leaks with no guesswork — and stop the water damage."
  },
  {
    icon: <Sensors />,
    title: "Sewer Line & Camera Inspection",
    desc:
      "Video camera diagnostics, trenchless repair, and full sewer line replacement when needed."
  },
  {
    icon: <Build />,
    title: "Repipes & Fixtures",
    desc:
      "Whole-home repiping, faucets, toilets, garbage disposals, and gas lines installed right."
  }
];

const AREAS = [
  {
    county: "Riverside County",
    cities: [
      "Riverside",
      "Corona",
      "Moreno Valley",
      "Eastvale",
      "Jurupa Valley",
      "Norco"
    ]
  },
  {
    county: "San Bernardino County",
    cities: [
      "San Bernardino",
      "Rancho Cucamonga",
      "Fontana",
      "Ontario",
      "Chino",
      "Rialto"
    ]
  },
  {
    county: "Orange County",
    cities: [
      "Anaheim",
      "Santa Ana",
      "Irvine",
      "Orange",
      "Fullerton",
      "Yorba Linda"
    ]
  }
];

const WHY = [
  {
    icon: <Verified />,
    title: "Licensed, Bonded & Insured",
    desc:
      "CA-licensed plumbers and background-checked techs you can trust in your home."
  },
  {
    icon: <AttachMoney />,
    title: "Upfront, Honest Pricing",
    desc:
      "Flat-rate quotes before any work starts. No surprises, no hidden fees."
  },
  {
    icon: <Schedule />,
    title: "Fast, Same-Day Service",
    desc:
      "Fully stocked trucks and tight routing mean most jobs are done the same day."
  },
  {
    icon: <ThumbUp />,
    title: "100% Satisfaction Guarantee",
    desc: "We stand behind our workmanship with a guarantee on every repair."
  }
];

const REVIEWS = [
  {
    text:
      "Called at 11pm with a flooded kitchen. They had someone out in under an hour and fixed it fast. Lifesavers.",
    name: "Marisol G.",
    city: "Riverside, CA"
  },
  {
    text:
      "Honest quote, no upsell, and my new water heater was installed the same afternoon. Highly recommend.",
    name: "David R.",
    city: "Rancho Cucamonga, CA"
  },
  {
    text:
      "Cleared a main line clog two other companies couldn't. Professional, clean, and fairly priced.",
    name: "Karen T.",
    city: "Anaheim, CA"
  }
];

const encode = data =>
  Object.keys(data)
    .map(k => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
    .join("&");

const Home = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    address: "",
    message: ""
  });
  const [botField, setBotField] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim() || !formData.phone.trim()) {
      setError("Please enter your name and phone number so we can reach you.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "lead-form",
          "bot-field": botField,
          ...formData
        })
      });

      if (!response.ok) throw new Error("Submission failed");

      // Fire Google Ads / GA4 lead conversion if gtag is present.
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          event_category: "lead-form",
          event_label: formData.service || "general"
        });
      }

      setSuccess(true);
      setFormData({
        name: "",
        phone: "",
        email: "",
        service: "",
        address: "",
        message: ""
      });
    } catch (err) {
      setError(
        "Something went wrong sending your request. Please call us at " +
          PHONE +
          " and we'll help right away."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      {/* ===================== HERO ===================== */}
      <Hero>
        <HeroInner>
          <HeroCopy>
            <Eyebrow>
              <Bolt fontSize="small" /> 24/7 Emergency Plumbers — On Call Now
            </Eyebrow>
            <HeroTitle>
              Emergency Plumber in{" "}
              <span>Riverside, San Bernardino &amp; Orange County</span>
            </HeroTitle>
            <HeroSubtitle>
              Fast, upfront, and reliable plumbing repair from licensed local
              pros. Burst pipes, clogged drains, water heaters, and leaks —
              fixed right the first time.
            </HeroSubtitle>

            <HeroBullets>
              <li>
                <CheckCircle fontSize="small" /> Same-day service
              </li>
              <li>
                <CheckCircle fontSize="small" /> Upfront flat-rate pricing
              </li>
              <li>
                <CheckCircle fontSize="small" /> Licensed &amp; insured
              </li>
              <li>
                <CheckCircle fontSize="small" /> Free, no-obligation estimates
              </li>
            </HeroBullets>

            <HeroCtas>
              <PrimaryCall href={PHONE_HREF}>
                <Phone /> Call {PHONE}
              </PrimaryCall>
              <GhostCta href="#quote">Get a Free Estimate</GhostCta>
            </HeroCtas>

            <HeroRating>
              <span className="stars">★★★★★</span>
              <span>
                <strong>4.9/5</strong> from 200+ local homeowners
              </span>
            </HeroRating>
          </HeroCopy>

          {/* ===== LEAD FORM ===== */}
          {success
            ? <SuccessCard id="quote">
                <div className="check">
                  <CheckCircle style={{ fontSize: "2.4rem" }} />
                </div>
                <h2>Request Received!</h2>
                <p>
                  Thanks — a member of our team will call you shortly to confirm
                  your appointment.
                </p>
                <p>
                  We typically respond within 15 minutes during business hours.
                </p>
                <p className="urgent">
                  Need help now? Call <a href={PHONE_HREF}>{PHONE}</a>
                </p>
                <SubmitButton
                  style={{ marginTop: "1.25rem" }}
                  onClick={() => setSuccess(false)}
                >
                  Submit Another Request
                </SubmitButton>
              </SuccessCard>
            : <FormCard
                id="quote"
                name="lead-form"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
              >
                {/* Netlify needs these for JS-rendered forms */}
                <input type="hidden" name="form-name" value="lead-form" />
                <Honeypot>
                  <label>
                    Don’t fill this out:{" "}
                    <input
                      name="bot-field"
                      value={botField}
                      onChange={e => setBotField(e.target.value)}
                    />
                  </label>
                </Honeypot>

                <FormHeading>
                  <h2>Get Your Free Estimate</h2>
                  <p>Fast response · No obligation</p>
                </FormHeading>

                <OfferTag>
                  💧 $50 OFF any repair — mention this online offer
                </OfferTag>

                {error &&
                  <ErrorMessage>
                    {error}
                  </ErrorMessage>}

                <Field>
                  <label htmlFor="name">Full Name*</label>
                  <input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                  />
                </Field>

                <Row>
                  <Field>
                    <label htmlFor="phone">Phone*</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      autoComplete="tel"
                    />
                  </Field>
                  <Field>
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                    />
                  </Field>
                </Row>

                <Field>
                  <label htmlFor="service">What do you need help with?</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                  >
                    <option value="">Select a service…</option>
                    <option>Emergency / Burst Pipe</option>
                    <option>Drain Cleaning / Clog</option>
                    <option>Water Heater</option>
                    <option>Leak Detection / Repair</option>
                    <option>Sewer Line</option>
                    <option>Repipe / Fixtures</option>
                    <option>Other</option>
                  </select>
                </Field>

                <Field>
                  <label htmlFor="address">Service Address / City</label>
                  <input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="City or ZIP"
                    autoComplete="address-level2"
                  />
                </Field>

                <Field>
                  <label htmlFor="message">
                    Briefly describe the issue (optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                  />
                </Field>

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? "Sending…" : "Request My Free Estimate"}
                </SubmitButton>

                <PrivacyNote>
                  🔒 Your info is secure and never shared. By submitting, you
                  agree to be contacted about your request.
                </PrivacyNote>
              </FormCard>}
        </HeroInner>
      </Hero>

      {/* ===================== TRUST STRIP ===================== */}
      <TrustStrip>
        <TrustInner>
          <div className="stat">
            <strong>24/7</strong>
            <span>Emergency Service</span>
          </div>
          <div className="stat">
            <strong>15min</strong>
            <span>Avg. Response</span>
          </div>
          <div className="stat">
            <strong>4.9★</strong>
            <span>200+ Reviews</span>
          </div>
          <div className="stat">
            <strong>100%</strong>
            <span>Satisfaction Guarantee</span>
          </div>
        </TrustInner>
      </TrustStrip>

      {/* ===================== SERVICES ===================== */}
      <Section id="services">
        <SectionInner>
          <SectionHead>
            <span className="kicker">Our Services</span>
            <h2>Full-Service Plumbing &amp; Rooter</h2>
            <p>
              From a leaky faucet to a full sewer replacement, our licensed
              plumbers handle it all — residential and commercial.
            </p>
          </SectionHead>
          <ServiceGrid>
            {SERVICES.map(s =>
              <ServiceCard key={s.title}>
                <div className="icon">
                  {s.icon}
                </div>
                <h3>
                  {s.title}
                </h3>
                <p>
                  {s.desc}
                </p>
              </ServiceCard>
            )}
          </ServiceGrid>
        </SectionInner>
      </Section>

      {/* ===================== SERVICE AREAS ===================== */}
      <Section id="areas" $wash>
        <SectionInner>
          <SectionHead>
            <span className="kicker">Service Areas</span>
            <h2>Proudly Serving the Inland Empire &amp; Orange County</h2>
            <p>
              Local plumbers based near you — fast arrival across three counties
              and surrounding cities.
            </p>
          </SectionHead>
          <AreaGrid>
            {AREAS.map(a =>
              <AreaCard key={a.county}>
                <div className="county">
                  <Place /> {a.county}
                </div>
                <ul>
                  {a.cities.map(c =>
                    <li key={c}>
                      {c}
                    </li>
                  )}
                </ul>
              </AreaCard>
            )}
          </AreaGrid>
        </SectionInner>
      </Section>

      {/* ===================== WHY US ===================== */}
      <Section id="why">
        <SectionInner>
          <SectionHead>
            <span className="kicker">Why Choose Us</span>
            <h2>Plumbing You Can Actually Trust</h2>
            <p>
              We earn 5-star reviews by being on time, honest about price, and
              getting it right the first time.
            </p>
          </SectionHead>
          <WhyGrid>
            {WHY.map(w =>
              <WhyItem key={w.title}>
                <div className="badge">
                  {w.icon}
                </div>
                <div>
                  <h3>
                    {w.title}
                  </h3>
                  <p>
                    {w.desc}
                  </p>
                </div>
              </WhyItem>
            )}
          </WhyGrid>
        </SectionInner>
      </Section>

      {/* ===================== REVIEWS ===================== */}
      <Section id="reviews" $wash>
        <SectionInner>
          <SectionHead>
            <span className="kicker">Reviews</span>
            <h2>What Local Homeowners Say</h2>
            <p>
              Rated 4.9 out of 5 across Riverside, San Bernardino, and Orange
              County.
            </p>
          </SectionHead>
          <ReviewGrid>
            {REVIEWS.map(r =>
              <ReviewCard key={r.name}>
                <div className="stars">★★★★★</div>
                <blockquote>
                  “{r.text}”
                </blockquote>
                <figcaption>
                  {r.name}
                  <span>
                    {r.city}
                  </span>
                </figcaption>
              </ReviewCard>
            )}
          </ReviewGrid>
        </SectionInner>
      </Section>

      {/* ===================== FINAL CTA ===================== */}
      <CtaBand>
        <h2>Plumbing Problem? We’re Ready 24/7.</h2>
        <p>
          Call now for fast service, or request your free estimate online and
          we’ll reach out right away.
        </p>
        <div className="cta-row">
          <PrimaryCall href={PHONE_HREF}>
            <Phone /> Call {PHONE}
          </PrimaryCall>
          <GhostCta href="#quote">Get a Free Estimate</GhostCta>
        </div>
      </CtaBand>

      {/* ===================== FOOTER ===================== */}
      <Footer>
        <div className="grid">
          <div>
            <div className="brand">Xpress Rooter &amp; Plumbing</div>
            <p>
              Licensed, bonded &amp; insured plumbing and rooter service for the
              Inland Empire and Orange County. Available 24/7 for emergencies.
            </p>
            <p>
              <a className="foot-call" href={PHONE_HREF}>
                📞 {PHONE}
              </a>
            </p>
          </div>
          <div>
            <h4>Services</h4>
            <ul>
              <li>
                <a href="#services">Emergency Plumbing</a>
              </li>
              <li>
                <a href="#services">Drain Cleaning &amp; Rooter</a>
              </li>
              <li>
                <a href="#services">Water Heaters</a>
              </li>
              <li>
                <a href="#services">Leak Detection</a>
              </li>
              <li>
                <a href="#services">Sewer Line Repair</a>
              </li>
            </ul>
          </div>
          <div>
            <h4>Service Areas</h4>
            <ul>
              <li>Riverside County</li>
              <li>San Bernardino County</li>
              <li>Orange County</li>
            </ul>
            <h4 style={{ marginTop: "1rem" }}>Hours</h4>
            <p>Open 24 hours · 7 days a week</p>
          </div>
        </div>
        <div className="legal">
          © {new Date().getFullYear()} Xpress Rooter &amp; Plumbing. All rights
          reserved. · CA Licensed, Bonded &amp; Insured
        </div>
      </Footer>

      {/* ===================== STICKY MOBILE BAR ===================== */}
      <MobileCallBar>
        <a className="call" href={PHONE_HREF}>
          <Phone fontSize="small" /> Call Now
        </a>
        <a className="quote" href="#quote">
          <Star fontSize="small" /> Free Estimate
        </a>
      </MobileCallBar>
    </Page>
  );
};

export default Home;
